import { IonAlert, IonModal, useIonViewDidEnter } from '@ionic/react'
import { ConfirmPatientReportParams } from 'domain/usecases/confirm-patient-report'
import { PatientsService } from 'infra/services/patients-service'
import { ReportsService } from 'infra/services/reports-service'
import { makeModalNewPatientValidations } from 'main/factories/builders/modal-new-patient-validation-factory'
import React, { useState } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import { modalNewReportAtom, reportPatientAtom } from './modal-new-report-atom'
import './modal-new-report.scss'
import ConfirmNewPatient from './organisms/confirm-new-patient'
import ConfirmPatient from './organisms/confirm-patient'
import NewReport from './organisms/new-report'
import { UserInfo, UserRole } from 'infra/services/usecases/get-user-info'
import { ClinicsService } from 'infra/services/clinics-service'

type ModalNewReportProps = React.ComponentPropsWithoutRef<typeof IonModal> & {
  modal: React.RefObject<HTMLIonModalElement>
  trigger: string
  reportsService: ReportsService
  patientsService: PatientsService
  clinicsService: ClinicsService
  userInfo: UserInfo
  notNeededToConfirm: boolean
  onUnconfirmedPatient?: (confirmPatient: ConfirmPatientReportParams) => void
  onCreateReport?: (reportId: string, oncologistId: string) => void
}
export type ModalNewReportSteps = 'new-report' | 'confirm-patient' | 'confirm-new-patient'

export const ModalNewReport: React.FC<ModalNewReportProps> = (props) => {
  const [step, setStep] = useState<ModalNewReportSteps>('new-report')
  const { reportExtract } = useRecoilValue(modalNewReportAtom)
  const [confirmPatient, setConfirmPatient] = useRecoilState(reportPatientAtom)
  const [alertOpen, setAlertOpen] = useState(false)
  const [shouldReload, setShouldReload] = useState(false)

  useIonViewDidEnter(() => {
    setStep('new-report')
  })

  const onWillDismiss = (params: { shouldReload: boolean }) => {
    setStep('new-report')
    if (params.shouldReload) setAlertOpen(true)
    setShouldReload(params.shouldReload)
    props.modal.current?.dismiss(params)
    setConfirmPatient({
      reportId: '',
      name: '',
      MRN: '',
      nameError: '',
      mrnError: '',
      oncologist: '',
      oncologistId: '',
      oncologistError: '',
    })

  }

  const handleConfirmPatient = (reportId: string) => {
    props.onUnconfirmedPatient ?
      props.onUnconfirmedPatient({
        ...confirmPatient,
        reportId: reportId,
      }) :
      props.onCreateReport &&
      props.onCreateReport(reportId, confirmPatient.oncologistId || '')
  }

  const changeStep = (step: ModalNewReportSteps) => {
    setConfirmPatient({
      ...confirmPatient,
      name: '',
      nameError: ''
    })
    setStep(step)
  }

  return (
    <>
      <IonModal ref={props.modal} {...props} backdropDismiss={false} className="modal-new-report-wrapper">
        {
          {
            'new-report': (
              <NewReport
                isPatientAlreadyConfirmed={props.notNeededToConfirm}
                clinicsService={props.clinicsService}
                userInfo={props.userInfo}
                reportsService={props.reportsService}
                onCancel={() => onWillDismiss({ shouldReload: false })}
                onDidUpload={({ step, info }) => {
                  if (props.notNeededToConfirm) {
                    handleConfirmPatient(info.report_id as string)
                  } else setStep(step)
                }}
                onUploadError={() => null}
              />
            ),
            'confirm-patient': (
              <ConfirmPatient
                userInfo={props.userInfo}
                patientsService={props.patientsService}
                reportsService={props.reportsService}
                clinicsService={props.clinicsService}
                dismiss={() => onWillDismiss({ shouldReload: false })}
                onNewPatient={() => changeStep('confirm-new-patient')}
                onConfirmPatient={() => onWillDismiss({ shouldReload: true })}
              />
            ),
            'confirm-new-patient': (
              <ConfirmNewPatient
                patientsService={props.patientsService}
                reportsService={props.reportsService}
                userInfo={props.userInfo}
                clinicsService={props.clinicsService}
                dismiss={() => onWillDismiss({ shouldReload: false })}
                onMergeExisting={() => changeStep('confirm-patient')}
                onConfirmNewPatient={() => onWillDismiss({ shouldReload: true })}
                validations={makeModalNewPatientValidations()}
              />
            ),
          }[step]
        }
      </IonModal>
      <IonAlert
        isOpen={alertOpen}
        onDidDismiss={() => {
          setAlertOpen(false)
        }}
        header="Successfully created report"
        message="Your report is in review. You will see it in the dashboard when it is approved."
        buttons={[
          { text: 'Close', role: 'cancel', handler: () => setAlertOpen(false) }
        ]}
      ></IonAlert>
    </>
  )
}
